/*
 * Footer newsletter -> Klaviyo Client API subscription.
 * Sends the storefront locale (e.g. "nl-BE") so Klaviyo flows can pick the email language.
 * Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
 */
class KlaviyoNewsletterForm extends HTMLElement {
  static API_URL = "https://a.klaviyo.com/client/subscriptions/";
  static API_REVISION = "2025-01-15";
  static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button[type="submit"]');
    this.errorBox = this.querySelector("[data-error]");
    this.errorText = this.querySelector("[data-error-text]");
    this.successBox = this.querySelector("[data-success]");

    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener("submit", this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  async onSubmit(event) {
    event.preventDefault();
    this.hideMessages();

    const email = this.input.value.trim();
    if (!KlaviyoNewsletterForm.EMAIL_PATTERN.test(email)) {
      this.showError(this.dataset.invalidMessage);
      this.input.focus();
      return;
    }

    this.setLoading(true);
    try {
      let response = await this.subscribe(email, true);
      // Fallback in case the API revision rejects the built-in locale attribute.
      if (response.status === 400) {
        console.warn("[klaviyo-newsletter] locale attribute rejected, retrying without it", await response.text());
        response = await this.subscribe(email, false);
      }
      if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);

      this.showSuccess();
    } catch (error) {
      console.error("[klaviyo-newsletter]", error);
      this.showError(this.dataset.errorMessage);
    } finally {
      this.setLoading(false);
    }
  }

  subscribe(email, withLocaleAttribute) {
    const { companyId, listId, locale, language, country, source } = this.dataset;

    const profileAttributes = {
      email,
      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
      properties: {
        signup_source: source,
        signup_locale: locale,
        signup_language: language,
        signup_country: country,
      },
    };
    if (withLocaleAttribute) profileAttributes.locale = locale;

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: `Footer newsletter (${locale})`,
          profile: { data: { type: "profile", attributes: profileAttributes } },
        },
        relationships: { list: { data: { type: "list", id: listId } } },
      },
    };

    const url = `${KlaviyoNewsletterForm.API_URL}?company_id=${encodeURIComponent(companyId)}`;
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        Accept: "application/vnd.api+json",
        revision: KlaviyoNewsletterForm.API_REVISION,
      },
      body: JSON.stringify(payload),
    });
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute("aria-busy", String(isLoading));
  }

  hideMessages() {
    this.errorBox.hidden = true;
    this.successBox.hidden = true;
  }

  showError(message) {
    this.errorText.textContent = message;
    this.errorBox.hidden = false;
  }

  showSuccess() {
    this.form.reset();
    this.successBox.hidden = false;
    this.successBox.querySelector("[tabindex]")?.focus();
  }
}

if (!customElements.get("klaviyo-newsletter-form")) {
  customElements.define("klaviyo-newsletter-form", KlaviyoNewsletterForm);
}
